import React, { useEffect, useState } from "react";
import { Lead, LeadStageType } from "@/context/LeadContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import GuestLeadView from "@/components/leads/GuestLeadView";
import LeadQuickActions from "@/components/partners/LeadQuickActions";
import { parseLeadFormAnswers } from '@/utils/leadFormAnswers';

interface LeadDetailsModalProps {
  leadId: string | null;
  onClose: () => void;
  partnerId?: string;
  onViewed?: (leadId: string) => void;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
  leadId,
  onClose,
  partnerId,
  onViewed,
}) => {
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }

    const fetchLead = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/lead-detail.php?id=${encodeURIComponent(leadId)}`, {
          credentials: "include",
          headers: { "X-CSRF-Token": sessionStorage.getItem("csrf_token") || "" },
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.error?.message || "Lead fetch failed");
        const data = body.data;

        if (!data) {
          setError("Η επαφή δε βρέθηκε.");
          return;
        }

        const fetchedLead: Lead = {
          id: data.id,
          name: data.name,
          phone: data.phone,
          email: data.email || null,
          address: data.address,
          postalCode: data.postal_code,
          service: data.service,
          timeframe: data.timeframe,
          comments: data.comments,
          formAnswers: parseLeadFormAnswers(data.form_answers),
          stage: (data.stage || "Νέο") as LeadStageType,
          partnerId: data.partner_id,
          partnerName: data.partner_name,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          smsStatus: data.sms_sent === null ? null : (Number(data.sms_sent) === 1 ? 'sent' : 'failed'),
          viewed: Number(data.link_opened || 0) === 1,
          viewedAt: data.viewed_at,
          latitude: data.latitude,
          longitude: data.longitude,
          campaign: data.campaign,
          source: data.source,
          fbLeadId: data.fb_lead_id,
        };

        setLead(fetchedLead);

        // Mark as viewed (track from dashboard)
        if (partnerId && !fetchedLead.viewed) {
          try {
            await fetch("/api/mark-lead-viewed.php", {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": sessionStorage.getItem("csrf_token") || "",
              },
              body: JSON.stringify({
                lead_id: leadId,
                partner_id: partnerId,
                source: "dashboard",
              }),
            });
            onViewed?.(leadId);
          } catch (err) {
            console.error("Failed to mark lead as viewed:", err);
          }
        }
      } catch (err) {
        console.error("Failed to fetch lead:", err);
        setError("Σφάλμα κατά τη φόρτωση των στοιχείων επαφής.");
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [leadId, partnerId, onViewed]);

  const handleStageUpdate = async (newStage: LeadStageType) => {
    if (!lead) return;

    try {
      const response = await fetch("/api/update-lead-stage.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": sessionStorage.getItem("csrf_token") || "",
        },
        body: JSON.stringify({
          lead_id: lead.id,
          stage: newStage,
          partner_id: partnerId,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = typeof body?.error === "string"
          ? body.error
          : body?.error?.message;
        throw new Error(message || "Η αλλαγή σταδίου απέτυχε.");
      }

      // Update local state
      setLead({ ...lead, stage: newStage });
    } catch (err) {
      console.error("Failed to update stage:", err);
      throw err;
    }
  };

  return (
    <Dialog open={!!leadId} onOpenChange={onClose}>
      <DialogContent className="inset-x-0 bottom-0 top-auto max-h-[94dvh] w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-b-none rounded-t-3xl p-4 sm:left-1/2 sm:top-1/2 sm:h-[92dvh] sm:max-h-[92dvh] sm:w-[calc(100vw-3rem)] sm:max-w-5xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Στοιχεία Επαφής</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
          </div>
        ) : lead ? (
          <div className="py-4 space-y-4">
            {/* Quick contact actions at top for fast partner response */}
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 sm:p-4">
              <p className="text-xs font-medium text-slate-700 mb-2">
                Γρήγορη Επικοινωνία
              </p>
              <LeadQuickActions
                phone={lead.phone}
                email={lead.email || undefined}
                name={lead.name}
                service={lead.service}
                partnerName={lead.partnerName || undefined}
              />
            </div>

            <GuestLeadView
              lead={lead}
              onUpdateStage={handleStageUpdate}
              showThankYou={false}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailsModal;
